import axiosInstance from './AxiosInstance';

/**
 * API tipada del módulo INSTANCIA MUNICIPAL DE LA MUJER (IMM).
 * Base backend: `/imm/*` (axiosInstance ya antepone `/api`).
 *
 * INFORMACIÓN SENSIBLE: las vistas admin deben cuidar no exponer datos de más
 * en los listados (CURP, domicilio exacto, situación, notas, etc.).
 */

const BASE = '/imm';

/** URL absoluta de un archivo servido en /uploads/imm/... */
export const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
export const fileUrl = (ruta?: string | null) => (ruta ? `${API_URL}${ruta}` : '');

// ==========================================================================
// INTERFACES
// ==========================================================================

export interface ImmArchivo {
  id: number;
  tipo?: string; // solo expediente: INE|CURP|ComprobanteDomicilio|DocumentoCaso|Evidencia
  momento?: string | null;
  nombreOriginal?: string | null;
  rutaArchivo: string;
  mimeType?: string | null;
  tamanoBytes?: number | string | null;
  descripcion?: string | null;
  activo?: boolean;
  createdAt?: string;
}

export interface ImmExpediente {
  id: number;
  folio: string;
  tokenPublico?: string;
  nombre: string;
  apPaterno?: string | null;
  apMaterno?: string | null;
  fechaNacimiento?: string | null;
  edad?: number | null;
  curp?: string | null;
  estadoCivil?: string | null;
  escolaridad?: string | null;
  ocupacion?: string | null;
  telefono?: string | null;
  celular?: string | null;
  email?: string | null;
  // ubicación
  estado?: string | null;
  municipio?: string | null;
  localidadId?: number | null;
  localidad?: string | null;
  colonia?: string | null;
  codigoPostal?: string | null;
  tipoVialidad?: string | null;
  vialidad?: string | null;
  numeroExterior?: string | null;
  numeroInterior?: string | null;
  referencias?: string | null;
  latitud?: number | null;
  longitud?: number | null;
  urlMaps?: string | null;
  // adicional
  numeroHijos?: number | null;
  contactoEmergenciaNombre?: string | null;
  contactoEmergenciaTelefono?: string | null;
  observaciones?: string | null;
  estatus: string;
  activo?: boolean;
  createdAt?: string;
  updatedAt?: string;
  archivos?: ImmArchivo[];
}

export type LineaTiempoTipo =
  | 'Atencion' | 'Caso' | 'Canalizacion' | 'Decision'
  | 'OrdenProteccion' | 'CitaPsicologica' | 'Constancia';

export interface LineaTiempoEvento {
  tipo: LineaTiempoTipo;
  id: number;
  fecha: string;
  titulo: string;
  estatus?: string;
}

export interface ImmAtencion {
  id: number;
  expedienteId: number;
  fecha: string;
  hora?: string | null;
  medioLlegada: string; // Presencial|Telefono|WhatsApp|Correo|Canalizacion|PortalCiudadano
  motivo?: string | null;
  descripcion?: string | null;
  tipoApoyo?: string | null;
  area?: string | null;
  servidorPublico?: string | null;
  observaciones?: string | null;
  orientacion?: boolean;
  asesoria?: boolean;
  acompanamiento?: boolean;
  canalizacion?: boolean;
  acudioAcompanada?: boolean;
  asistioConMenores?: boolean;
  datosAcompanantes?: string | null;
  estatus: string;
  activo?: boolean;
  createdAt?: string;
  expediente?: Pick<ImmExpediente, 'id' | 'folio' | 'nombre' | 'apPaterno' | 'apMaterno'>;
}

export interface ImmCaso {
  id: number;
  expedienteId: number;
  fechaIncidente?: string | null;
  tipoViolenciaId?: number | null;
  tipoViolencia?: { id: number; descripcion: string } | null;
  nivelRiesgo: string; // Bajo|Medio|Alto
  descripcion?: string | null;
  medidasProteccion?: string | null;
  acciones?: string | null;
  dependenciasInvolucradas?: string | null;
  observaciones?: string | null;
  estatus: string; // Abierto|EnSeguimiento|Cerrado|Anulado
  activo?: boolean;
  createdAt?: string;
  historial?: ImmCasoHistorial[];
  archivos?: ImmArchivo[];
  expediente?: Pick<ImmExpediente, 'id' | 'folio' | 'nombre'>;
}

export interface ImmCasoHistorial {
  id: number;
  casoId: number;
  fecha: string;
  usuario?: string | null;
  accion: string;
  valoresAntJson?: string | null;
  valoresNuevJson?: string | null;
  createdAt?: string;
}

export interface ImmCanalizacion {
  id: number;
  expedienteId: number;
  folio: string;
  fecha: string;
  motivo?: string | null;
  descripcionHecho?: string | null;
  solicitud?: string | null;
  dependenciaDestino?: string | null;
  observaciones?: string | null;
  decisionDenuncia?: string | null;
  firmaUsuaria?: boolean;
  firmaPersonalImm?: boolean;
  rutaPdfGenerado?: string | null;
  estatus: string;
  activo?: boolean;
  createdAt?: string;
  expediente?: Pick<ImmExpediente, 'id' | 'folio' | 'nombre'>;
}

export interface ImmBanavim {
  id: number;
  expedienteId: number;
  folioBanavim?: string | null;
  fechaRegistro?: string | null;
  observaciones?: string | null;
  canalizacionRealizada?: boolean;
  acompanamientoRealizado?: boolean;
  estatus: string;
  activo?: boolean;
  createdAt?: string;
  expediente?: Pick<ImmExpediente, 'id' | 'folio' | 'nombre'>;
}

export interface ImmOrden {
  id: number;
  expedienteId: number;
  numeroExpediente?: string | null;
  autoridadEmisora?: string | null;
  fechaEmision?: string | null;
  fechaVencimiento?: string | null;
  motivo?: string | null;
  tipoMedida?: string | null;
  observaciones?: string | null;
  causaId?: number | null;
  audienciaId?: number | null;
  detencionId?: number | null;
  estatus: string; // Vigente|Vencida|Cancelada
  activo?: boolean;
  createdAt?: string;
  expediente?: Pick<ImmExpediente, 'id' | 'folio' | 'nombre'>;
}

export interface ImmDecision {
  id: number;
  expedienteId: number;
  fecha: string;
  tipoDecision: string; // DeseaDenunciar|NoDeseaDenunciar|SoloOrientacion|Acompanamiento|Canalizacion|Seguimiento
  usuarioRegistra?: string | null;
  observaciones?: string | null;
  activo?: boolean;
  createdAt?: string;
  expediente?: Pick<ImmExpediente, 'id' | 'folio' | 'nombre'>;
}

export interface ImmSolicitud {
  id: number;
  folio: string;
  tokenPublico?: string;
  nombre?: string | null;
  telefono?: string | null;
  email?: string | null;
  situacion?: string | null;
  tipoApoyo?: string | null;
  estado?: string | null;
  municipio?: string | null;
  localidadId?: number | null;
  localidad?: string | null;
  colonia?: string | null;
  codigoPostal?: string | null;
  tipoVialidad?: string | null;
  vialidad?: string | null;
  numeroExterior?: string | null;
  numeroInterior?: string | null;
  referencias?: string | null;
  latitud?: number | null;
  longitud?: number | null;
  urlMaps?: string | null;
  fecha: string;
  hora?: string | null;
  atencionInmediata?: boolean;
  disponibilidad?: string | null;
  expedienteId?: number | null;
  estatus: string; // Recibida|Vinculada|Atendida|Cerrada
  activo?: boolean;
  createdAt?: string;
  archivos?: ImmArchivo[];
}

export interface ImmCita {
  id: number;
  expedienteId?: number | null;
  fecha: string;
  horaInicio?: string | null;
  horaFin?: string | null;
  psicologo?: string | null;
  motivo?: string | null;
  agendaCompartidaId?: number | null;
  origen: string; // IMM|DIF
  estatus: string; // Programada|Reagendada|Cancelada|Atendida
  activo?: boolean;
  createdAt?: string;
  sesiones?: ImmSesion[];
}

export interface ImmSesion {
  id: number;
  citaId: number;
  notasSeguimiento?: string | null;
  confidencial?: boolean;
  activo?: boolean;
  createdAt?: string;
}

export interface ImmDisponibilidad {
  fecha: string;
  ocupados: Array<{ id: number; horaInicio?: string | null; psicologo?: string | null; estatus: string }>;
  horariosOcupados: string[];
  horariosLibres: string[];
}

export interface ImmTaller {
  id: number;
  nombre: string;
  tipo: string; // Taller|Curso|Conferencia|Platica|Actividad|Caminata|Evento
  fecha?: string | null;
  horario?: string | null;
  sede?: string | null;
  instructor?: string | null;
  cupo?: number | null;
  descripcion?: string | null;
  observaciones?: string | null;
  publicadoPortal?: boolean;
  estatus: string;
  activo?: boolean;
  createdAt?: string;
  archivos?: ImmArchivo[];
  inscripciones?: ImmInscripcion[];
  _count?: { inscripciones: number };
}

export interface ImmInscripcion {
  id: number;
  tallerId: number;
  nombre: string;
  telefono?: string | null;
  email?: string | null;
  origenPortal?: boolean;
  expedienteId?: number | null;
  estatus: string; // Inscrita|Confirmada|Asistio|NoAsistio|Cancelo
  activo?: boolean;
  createdAt?: string;
}

export interface ImmListaAsistencia {
  taller: Pick<ImmTaller, 'id' | 'nombre' | 'fecha' | 'horario' | 'sede' | 'instructor'> | null;
  total: number;
  inscripciones: Array<Pick<ImmInscripcion, 'id' | 'nombre' | 'telefono' | 'estatus' | 'origenPortal'>>;
}

export interface ImmAgenda {
  id: number;
  fecha: string;
  hora?: string | null;
  lugar?: string | null;
  tema?: string | null;
  tipo: string; // Reunion|Oficio|Invitacion|Conferencia|Evento|Cita
  observaciones?: string | null;
  estatus: string;
  activo?: boolean;
  createdAt?: string;
  archivos?: ImmArchivo[];
}

export interface ImmConstancia {
  id: number;
  folio: string;
  tokenPublico?: string;
  expedienteId: number;
  fecha: string;
  tipoDocumento?: string | null;
  motivo?: string | null;
  textoInstitucional?: string | null;
  servidorPublico?: string | null;
  cargo?: string | null;
  dependenciaEmisora?: string;
  rutaPdf?: string | null;
  estatus: string; // Vigente|Cancelada
  activo?: boolean;
  createdAt?: string;
  expediente?: Pick<ImmExpediente, 'id' | 'folio' | 'nombre'>;
}

export interface ImmTipoViolencia {
  id: number;
  codigo: string;
  descripcion: string;
  orden?: number;
  activo?: boolean;
}

// Dashboard
export interface DashResumen {
  expedientes: number;
  casosAbiertos: number;
  atenciones: number;
  canalizaciones: number;
  ordenesVigentes: number;
  solicitudesRecibidas: number;
}
export interface DashAtendida { periodo: string; atenciones: number; mujeresUnicas: number; }
export interface DashTipoViolencia { tipoViolenciaId: number | null; tipoViolencia: string; total: number; }
export interface DashNivelRiesgo { nivelRiesgo: string; total: number; }
export interface DashLocalidad { localidad: string; total: number; }
export interface DashRangoEdad { rango: string; total: number; }
export interface DashServicios { totalAtenciones: number; orientacion: number; asesoria: number; acompanamiento: number; canalizacion: number; }
export interface DashCanalizacion { dependencia: string; total: number; }
export interface DashPsicologia { citasPorEstatus: Array<{ estatus: string; total: number }>; sesiones: number; }
export interface DashTalleres { totalTalleres: number; totalInscripciones: number; inscripcionesPorEstatus: Array<{ estatus: string; total: number }>; }

// ==========================================================================
// EXPEDIENTES  (núcleo)
// ==========================================================================

export const listarExpedientes = async (params?: { q?: string; estatus?: string; localidadId?: number }) =>
  (await axiosInstance.get<ImmExpediente[]>(`${BASE}/expedientes`, { params })).data;

export const obtenerExpediente = async (id: number) =>
  (await axiosInstance.get<ImmExpediente>(`${BASE}/expedientes/${id}`)).data;

export const crearExpediente = async (data: Partial<ImmExpediente>) =>
  (await axiosInstance.post<ImmExpediente>(`${BASE}/expedientes`, data)).data;

export const actualizarExpediente = async (id: number, data: Partial<ImmExpediente>) =>
  (await axiosInstance.put<ImmExpediente>(`${BASE}/expedientes/${id}`, data)).data;

export const eliminarExpediente = async (id: number) =>
  (await axiosInstance.delete(`${BASE}/expedientes/${id}`)).data;

export const obtenerLineaTiempo = async (id: number) =>
  (await axiosInstance.get<LineaTiempoEvento[]>(`${BASE}/expedientes/${id}/linea-tiempo`)).data;

export const listarArchivosExpediente = async (id: number) =>
  (await axiosInstance.get<ImmArchivo[]>(`${BASE}/expedientes/${id}/archivos`)).data;

export const subirArchivoExpediente = async (id: number, archivo: File, meta: { tipo?: string; momento?: string; descripcion?: string }) => {
  const form = new FormData();
  form.append('archivo', archivo);
  if (meta.tipo) form.append('tipo', meta.tipo);
  if (meta.momento) form.append('momento', meta.momento);
  if (meta.descripcion) form.append('descripcion', meta.descripcion);
  return (await axiosInstance.post<ImmArchivo>(`${BASE}/expedientes/${id}/archivos`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })).data;
};

export const eliminarArchivoExpediente = async (id: number, archivoId: number) =>
  (await axiosInstance.delete(`${BASE}/expedientes/${id}/archivos/${archivoId}`)).data;

// ==========================================================================
// ATENCIONES
// ==========================================================================

export const listarAtenciones = async (params?: { expedienteId?: number; medioLlegada?: string; estatus?: string }) =>
  (await axiosInstance.get<ImmAtencion[]>(`${BASE}/atenciones`, { params })).data;
export const obtenerAtencion = async (id: number) =>
  (await axiosInstance.get<ImmAtencion>(`${BASE}/atenciones/${id}`)).data;
export const crearAtencion = async (data: Partial<ImmAtencion>) =>
  (await axiosInstance.post<ImmAtencion>(`${BASE}/atenciones`, data)).data;
export const actualizarAtencion = async (id: number, data: Partial<ImmAtencion>) =>
  (await axiosInstance.put<ImmAtencion>(`${BASE}/atenciones/${id}`, data)).data;
export const eliminarAtencion = async (id: number) =>
  (await axiosInstance.delete(`${BASE}/atenciones/${id}`)).data;

// ==========================================================================
// CASOS  (historial append-only, baja lógica)
// ==========================================================================

export const listarCasos = async (params?: { expedienteId?: number; nivelRiesgo?: string; estatus?: string; tipoViolenciaId?: number }) =>
  (await axiosInstance.get<ImmCaso[]>(`${BASE}/casos`, { params })).data;
export const obtenerCaso = async (id: number) =>
  (await axiosInstance.get<ImmCaso>(`${BASE}/casos/${id}`)).data;
export const obtenerHistorialCaso = async (id: number) =>
  (await axiosInstance.get<ImmCasoHistorial[]>(`${BASE}/casos/${id}/historial`)).data;
export const crearCaso = async (data: Partial<ImmCaso>) =>
  (await axiosInstance.post<ImmCaso>(`${BASE}/casos`, data)).data;
export const actualizarCaso = async (id: number, data: Partial<ImmCaso>) =>
  (await axiosInstance.put<ImmCaso>(`${BASE}/casos/${id}`, data)).data;
export const bajaCaso = async (id: number, motivo?: string) =>
  (await axiosInstance.delete(`${BASE}/casos/${id}`, { data: { motivo } })).data;
export const subirArchivoCaso = async (id: number, archivo: File, descripcion?: string) => {
  const form = new FormData();
  form.append('archivo', archivo);
  if (descripcion) form.append('descripcion', descripcion);
  return (await axiosInstance.post<ImmArchivo>(`${BASE}/casos/${id}/archivos`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })).data;
};

// ==========================================================================
// CANALIZACIONES
// ==========================================================================

export const listarCanalizaciones = async (params?: { expedienteId?: number; estatus?: string }) =>
  (await axiosInstance.get<ImmCanalizacion[]>(`${BASE}/canalizaciones`, { params })).data;
export const obtenerCanalizacion = async (id: number) =>
  (await axiosInstance.get<ImmCanalizacion>(`${BASE}/canalizaciones/${id}`)).data;
export const crearCanalizacion = async (data: Partial<ImmCanalizacion>) =>
  (await axiosInstance.post<ImmCanalizacion>(`${BASE}/canalizaciones`, data)).data;
export const actualizarCanalizacion = async (id: number, data: Partial<ImmCanalizacion>) =>
  (await axiosInstance.put<ImmCanalizacion>(`${BASE}/canalizaciones/${id}`, data)).data;
export const eliminarCanalizacion = async (id: number) =>
  (await axiosInstance.delete(`${BASE}/canalizaciones/${id}`)).data;
export const marcarPdfCanalizacion = async (id: number, rutaPdf: string) =>
  (await axiosInstance.post<ImmCanalizacion>(`${BASE}/canalizaciones/${id}/pdf`, { rutaPdf })).data;

/** Descarga el PDF real (application/pdf) de una canalización y lo abre en una pestaña nueva. */
export const descargarPdfCanalizacion = async (id: number) => {
  const res = await axiosInstance.get(`${BASE}/canalizaciones/${id}/pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// ==========================================================================
// BANAVIM
// ==========================================================================

export const listarBanavim = async (params?: { expedienteId?: number; estatus?: string }) =>
  (await axiosInstance.get<ImmBanavim[]>(`${BASE}/banavim`, { params })).data;
export const obtenerBanavim = async (id: number) =>
  (await axiosInstance.get<ImmBanavim>(`${BASE}/banavim/${id}`)).data;
export const crearBanavim = async (data: Partial<ImmBanavim>) =>
  (await axiosInstance.post<ImmBanavim>(`${BASE}/banavim`, data)).data;
export const actualizarBanavim = async (id: number, data: Partial<ImmBanavim>) =>
  (await axiosInstance.put<ImmBanavim>(`${BASE}/banavim/${id}`, data)).data;
export const eliminarBanavim = async (id: number) =>
  (await axiosInstance.delete(`${BASE}/banavim/${id}`)).data;

// ==========================================================================
// ORDENES DE PROTECCIÓN
// ==========================================================================

export const listarOrdenes = async (params?: { expedienteId?: number; estatus?: string }) =>
  (await axiosInstance.get<ImmOrden[]>(`${BASE}/ordenes`, { params })).data;
export const obtenerOrden = async (id: number) =>
  (await axiosInstance.get<ImmOrden>(`${BASE}/ordenes/${id}`)).data;
export const crearOrden = async (data: Partial<ImmOrden>) =>
  (await axiosInstance.post<ImmOrden>(`${BASE}/ordenes`, data)).data;
export const actualizarOrden = async (id: number, data: Partial<ImmOrden>) =>
  (await axiosInstance.put<ImmOrden>(`${BASE}/ordenes/${id}`, data)).data;
export const eliminarOrden = async (id: number) =>
  (await axiosInstance.delete(`${BASE}/ordenes/${id}`)).data;
export const vincularOrden = async (id: number, data: { causaId?: number; audienciaId?: number; detencionId?: number }) =>
  (await axiosInstance.post<ImmOrden>(`${BASE}/ordenes/${id}/vincular`, data)).data;
export const ordenesDesdeJuzgado = async (params?: { expedienteId?: number }) =>
  (await axiosInstance.get<ImmOrden[]>(`${BASE}/ordenes/desde-juzgado`, { params })).data;

// ==========================================================================
// DECISIONES (append-only, no editable)
// ==========================================================================

export const listarDecisiones = async (params?: { expedienteId?: number }) =>
  (await axiosInstance.get<ImmDecision[]>(`${BASE}/decisiones`, { params })).data;
export const obtenerDecision = async (id: number) =>
  (await axiosInstance.get<ImmDecision>(`${BASE}/decisiones/${id}`)).data;
export const crearDecision = async (data: Partial<ImmDecision>) =>
  (await axiosInstance.post<ImmDecision>(`${BASE}/decisiones`, data)).data;

// ==========================================================================
// SOLICITUDES (bandeja del portal)
// ==========================================================================

export const listarSolicitudes = async (params?: { estatus?: string; localidadId?: number; expedienteId?: number }) =>
  (await axiosInstance.get<ImmSolicitud[]>(`${BASE}/solicitudes`, { params })).data;
export const obtenerSolicitud = async (id: number) =>
  (await axiosInstance.get<ImmSolicitud>(`${BASE}/solicitudes/${id}`)).data;
export const crearSolicitud = async (data: FormData) =>
  (await axiosInstance.post<ImmSolicitud>(`${BASE}/solicitudes`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })).data;
export const actualizarSolicitud = async (id: number, data: Partial<ImmSolicitud>) =>
  (await axiosInstance.put<ImmSolicitud>(`${BASE}/solicitudes/${id}`, data)).data;
export const eliminarSolicitud = async (id: number) =>
  (await axiosInstance.delete(`${BASE}/solicitudes/${id}`)).data;
export const vincularExpedienteSolicitud = async (id: number, data: { expedienteId?: number }) =>
  (await axiosInstance.post<{ solicitud: ImmSolicitud; expedienteId: number }>(`${BASE}/solicitudes/${id}/vincular-expediente`, data)).data;

// ==========================================================================
// PSICOLOGÍA (citas + sesiones + disponibilidad)
// ==========================================================================

export const listarCitas = async (params?: { expedienteId?: number; estatus?: string; origen?: string; fecha?: string }) =>
  (await axiosInstance.get<ImmCita[]>(`${BASE}/psicologia`, { params })).data;
export const obtenerCita = async (id: number) =>
  (await axiosInstance.get<ImmCita>(`${BASE}/psicologia/${id}`)).data;
export const crearCita = async (data: Partial<ImmCita>) =>
  (await axiosInstance.post<ImmCita>(`${BASE}/psicologia`, data)).data;
export const reagendarCita = async (id: number, data: { fecha?: string; horaInicio?: string; horaFin?: string; psicologo?: string }) =>
  (await axiosInstance.put<ImmCita>(`${BASE}/psicologia/${id}/reagendar`, data)).data;
export const cancelarCita = async (id: number) =>
  (await axiosInstance.put<ImmCita>(`${BASE}/psicologia/${id}/cancelar`, {})).data;
export const atenderCita = async (id: number) =>
  (await axiosInstance.put<ImmCita>(`${BASE}/psicologia/${id}/atender`, {})).data;
export const registrarSesion = async (id: number, data: { notasSeguimiento?: string; confidencial?: boolean }) =>
  (await axiosInstance.post<ImmSesion>(`${BASE}/psicologia/${id}/sesiones`, data)).data;
export const disponibilidadPsicologia = async (fecha: string, psicologo?: string) =>
  (await axiosInstance.get<ImmDisponibilidad>(`${BASE}/psicologia/disponibilidad`, { params: { fecha, psicologo } })).data;

// ==========================================================================
// TALLERES (inscripciones + asistencia)
// ==========================================================================

export const listarTalleres = async (params?: { tipo?: string; estatus?: string; publicadoPortal?: boolean }) =>
  (await axiosInstance.get<ImmTaller[]>(`${BASE}/talleres`, { params })).data;
export const obtenerTaller = async (id: number) =>
  (await axiosInstance.get<ImmTaller>(`${BASE}/talleres/${id}`)).data;
export const crearTaller = async (data: Partial<ImmTaller>) =>
  (await axiosInstance.post<ImmTaller>(`${BASE}/talleres`, data)).data;
export const actualizarTaller = async (id: number, data: Partial<ImmTaller>) =>
  (await axiosInstance.put<ImmTaller>(`${BASE}/talleres/${id}`, data)).data;
export const eliminarTaller = async (id: number) =>
  (await axiosInstance.delete(`${BASE}/talleres/${id}`)).data;
export const publicarTaller = async (id: number, publicado: boolean) =>
  (await axiosInstance.put<ImmTaller>(`${BASE}/talleres/${id}/publicar`, { publicado })).data;
export const inscribirTaller = async (id: number, data: Partial<ImmInscripcion>) =>
  (await axiosInstance.post<ImmInscripcion>(`${BASE}/talleres/${id}/inscripciones`, data)).data;
export const marcarAsistencia = async (inscripcionId: number, estatus: string) =>
  (await axiosInstance.put<ImmInscripcion>(`${BASE}/talleres/inscripciones/${inscripcionId}/asistencia`, { estatus })).data;
export const obtenerListaAsistencia = async (id: number) =>
  (await axiosInstance.get<ImmListaAsistencia>(`${BASE}/talleres/${id}/lista-asistencia`)).data;

// ==========================================================================
// AGENDA
// ==========================================================================

export const listarAgenda = async (params?: { tipo?: string; estatus?: string }) =>
  (await axiosInstance.get<ImmAgenda[]>(`${BASE}/agenda`, { params })).data;
export const obtenerAgenda = async (id: number) =>
  (await axiosInstance.get<ImmAgenda>(`${BASE}/agenda/${id}`)).data;
export const crearAgenda = async (data: Partial<ImmAgenda>) =>
  (await axiosInstance.post<ImmAgenda>(`${BASE}/agenda`, data)).data;
export const actualizarAgenda = async (id: number, data: Partial<ImmAgenda>) =>
  (await axiosInstance.put<ImmAgenda>(`${BASE}/agenda/${id}`, data)).data;
export const eliminarAgenda = async (id: number) =>
  (await axiosInstance.delete(`${BASE}/agenda/${id}`)).data;

// ==========================================================================
// CONSTANCIAS
// ==========================================================================

export const listarConstancias = async (params?: { estatus?: string; expedienteId?: number }) =>
  (await axiosInstance.get<ImmConstancia[]>(`${BASE}/constancias`, { params })).data;
export const obtenerConstancia = async (id: number) =>
  (await axiosInstance.get<ImmConstancia>(`${BASE}/constancias/${id}`)).data;
export const crearConstancia = async (data: Partial<ImmConstancia>) =>
  (await axiosInstance.post<ImmConstancia>(`${BASE}/constancias`, data)).data;
export const actualizarConstancia = async (id: number, data: Partial<ImmConstancia>) =>
  (await axiosInstance.put<ImmConstancia>(`${BASE}/constancias/${id}`, data)).data;
export const cancelarConstancia = async (id: number) =>
  (await axiosInstance.delete(`${BASE}/constancias/${id}`)).data;

/** Descarga el PDF real (con código QR de validación) de una constancia y lo abre en pestaña nueva. */
export const descargarPdfConstancia = async (id: number) => {
  const res = await axiosInstance.get(`${BASE}/constancias/${id}/pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// ==========================================================================
// CATÁLOGOS
// ==========================================================================

export const listarTiposViolencia = async () =>
  (await axiosInstance.get<ImmTipoViolencia[]>(`${BASE}/catalogos/tipo-violencia`)).data;

// ==========================================================================
// DASHBOARD (SOLO agregados, sin nombres)
// ==========================================================================

/** Rango de fecha opcional aplicado a los agregados del tablero. */
export interface DashRango { desde?: string; hasta?: string; }

export const dashResumen = async (params?: DashRango) =>
  (await axiosInstance.get<DashResumen>(`${BASE}/dashboard/resumen`, { params })).data;
export const dashAtendidas = async (params?: { periodo?: 'dia' | 'mes' | 'anio'; desde?: string; hasta?: string }) =>
  (await axiosInstance.get<DashAtendida[]>(`${BASE}/dashboard/atendidas`, { params })).data;
export const dashCasosTipoViolencia = async (params?: DashRango) =>
  (await axiosInstance.get<DashTipoViolencia[]>(`${BASE}/dashboard/casos-tipo-violencia`, { params })).data;
export const dashCasosNivelRiesgo = async (params?: DashRango) =>
  (await axiosInstance.get<DashNivelRiesgo[]>(`${BASE}/dashboard/casos-nivel-riesgo`, { params })).data;
export const dashPorLocalidad = async (params?: DashRango) =>
  (await axiosInstance.get<DashLocalidad[]>(`${BASE}/dashboard/por-localidad`, { params })).data;
export const dashPorRangoEdad = async (params?: DashRango) =>
  (await axiosInstance.get<DashRangoEdad[]>(`${BASE}/dashboard/por-rango-edad`, { params })).data;
export const dashServicios = async (params?: DashRango) =>
  (await axiosInstance.get<DashServicios>(`${BASE}/dashboard/servicios`, { params })).data;
export const dashCanalizaciones = async (params?: DashRango) =>
  (await axiosInstance.get<DashCanalizacion[]>(`${BASE}/dashboard/canalizaciones`, { params })).data;
export const dashPsicologia = async (params?: DashRango) =>
  (await axiosInstance.get<DashPsicologia>(`${BASE}/dashboard/psicologia`, { params })).data;
export const dashTalleres = async (params?: DashRango) =>
  (await axiosInstance.get<DashTalleres>(`${BASE}/dashboard/talleres`, { params })).data;

// ==========================================================================
// LOCALIDADES (catálogo compartido del sistema)
// ==========================================================================

export interface Localidad { id: number; descripcion: string; }
export const listarLocalidades = async () =>
  (await axiosInstance.get<Localidad[]>(`/cLocalidadRoutes`)).data;
